import { useEffect, useMemo, useState } from "react";
import { useDocumentTitle } from "@uidotdev/usehooks";
import { useSearchParams } from "react-router-dom";
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Table from '@mui/joy/Table';
import Stack from '@mui/joy/Stack';
import Chip from '@mui/joy/Chip';
import Alert from '@mui/joy/Alert';
import Input from '@mui/joy/Input';
import Select from '@mui/joy/Select';
import Autocomplete from '@mui/joy/Autocomplete';
import Switch from '@mui/joy/Switch';
import Option from '@mui/joy/Option';
import Button from '@mui/joy/Button';
import Tooltip from '@mui/joy/Tooltip';
import Link from '@mui/joy/Link';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import IconButton from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import Subheader from "@components/layout/Subheader";
import Paginate from "@components/Paginate";
import MaestroLoader from "@components/MaestroLoader";
import UnrecoverableError from "@components/UnrecoverableError";
import toast from "react-hot-toast";
import { auditActionColor, auditEntityPath, auditEventKey, useGetAuditLog, useGetAuditLogFilters, useSetAuditDigestSubscription } from "@api/auditLogService";

const isoDate = (date) => date.toISOString().slice(0, 10);

const defaultFrom = (days) => {
    const date = new Date();
    date.setDate(date.getDate() - (days || 30));
    return isoDate(date);
};

const validDate = (value) => (value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null);

const parseTimestamp = (value) => {
    if(!value) {
        return null;
    }
    return new Date(String(value).replace(' ', 'T') + 'Z');
};

const displayTimestamp = (value, timeZone) => {
    const date = parseTimestamp(value);
    if(!date || isNaN(date.getTime())) {
        return value;
    }
    return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        ...(timeZone ? { timeZone } : {}),
    });
};

const displayValue = (value) => {
    if(value === null || value === undefined || value === '') {
        return <Typography fontSize="xs" textColor="neutral.400">empty</Typography>;
    }
    return <Typography fontSize="xs">{String(value)}</Typography>;
};

export default function AuditLogIndex() {
    useDocumentTitle('Audit Log | Maestro Community Manager');

    const [searchParams] = useSearchParams();

    const [page, setPage] = useState(1);
    const [expanded, setExpanded] = useState({});
    const [from, setFrom] = useState(validDate(searchParams.get('from')) || defaultFrom(30));
    const [to, setTo] = useState(validDate(searchParams.get('to')) || isoDate(new Date()));
    const [conductor, setConductor] = useState(null);
    const [community, setCommunity] = useState(null);
    const [groups, setGroups] = useState([]);
    const [tables, setTables] = useState([]);
    const [userSearch, setUserSearch] = useState('');
    const [actions, setActions] = useState([]);
    const [refreshToken, setRefreshToken] = useState(0);
    const perPage = 50;

    const { data: filters, isLoading: filtersLoading } = useGetAuditLogFilters(conductor?.id ?? null);
    const subscription = useSetAuditDigestSubscription();

    const toggleSubscription = (subscribed) => {
        subscription.mutate({ subscribed, conductor: conductor?.id ?? null }, {
            onSuccess: () => toast.success(subscribed
                ? 'You will get the weekly audit digest on Monday mornings.'
                : 'You have been unsubscribed from the weekly audit digest.'),
            onError: () => toast.error('Could not update your digest subscription.'),
        });
    };

    const params = useMemo(() => {
        const next = { from: `${from} 00:00:00`, to: `${to} 23:59:59` };

        if(conductor) next.conductor = conductor.id;
        if(community) next.communities = community.subdomain;
        if(groups.length) next.groups = groups.join(',');
        if(tables.length) next.tables = tables.join(',');
        if(userSearch.trim()) next.user_search = userSearch.trim();
        if(actions.length) next.actions = actions.join(',');
        if(refreshToken) next.refresh = refreshToken;

        return next;
    }, [from, to, conductor, community, groups, tables, userSearch, actions, refreshToken]);

    const [debouncedParams, setDebouncedParams] = useState(params);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedParams(params), 450);

        return () => clearTimeout(timer);
    }, [params]);

    const needsConductor = !!filters?.conductors?.length && !debouncedParams.conductor;

    const { data, isLoading, isError, isFetching } = useGetAuditLog(debouncedParams, {
        enabled: !filtersLoading && !needsConductor,
    });

    useEffect(() => {
        if(refreshToken && !isFetching) {
            setRefreshToken(0);
        }
    }, [data]);

    const availableTables = useMemo(() => {
        if(!filters?.groups) {
            return [];
        }

        const selected = groups.length
            ? filters.groups.filter((group) => groups.includes(group.key))
            : filters.groups;

        return selected.flatMap((group) => group.tables.map((table) => ({ ...table, group: group.label })));
    }, [filters, groups]);

    const resetPage = (setter) => (value) => {
        setPage(1);
        setter(value);
    };

    const toggleExpanded = (key) => {
        setExpanded((current) => ({ ...current, [key]: !current[key] }));
    };

    const clearFilters = () => {
        setPage(1);
        setCommunity(null);
        setGroups([]);
        setTables([]);
        setUserSearch('');
        setActions([]);
        setFrom(defaultFrom(filters?.default_range_days || 30));
        setTo(isoDate(new Date()));
    };

    if(isError) {
        return <UnrecoverableError/>;
    }

    const allEvents = data?.data || [];
    const meta = data?.meta || {};
    const total = allEvents.length;
    const lastPage = total > 0 ? Math.ceil(total / perPage) : 1;
    const currentPage = Math.min(page, lastPage);
    const events = allEvents.slice((currentPage - 1) * perPage, currentPage * perPage);
    const rangeFrom = total > 0 ? ((currentPage - 1) * perPage) + 1 : 0;
    const rangeTo = total > 0 ? Math.min(currentPage * perPage, total) : 0;
    const communityMap = meta.communities || {};
    const hasFilters = community || groups.length || tables.length || userSearch || actions.length;

    return (<>
        <Subheader
            title={'Audit Log'}
            color={'neutral'}
        >
            <Stack direction="row" spacing={2} alignItems="center">
                {!!filters?.can_subscribe && <Switch
                    size="sm"
                    checked={!!filters?.subscribed}
                    disabled={filtersLoading || subscription.isPending}
                    onChange={(e) => toggleSubscription(e.target.checked)}
                    startDecorator={<Typography fontSize="sm" whiteSpace="nowrap">Weekly digest</Typography>}
                />}
                <Paginate
                    tint={'neutral'}
                    from={rangeFrom}
                    to={rangeTo}
                    total={total}
                    currentPage={currentPage}
                    perPage={perPage}
                    lastPage={lastPage}
                    onChange={setPage}
                />
            </Stack>
        </Subheader>

        <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
            <Sheet
                variant="outlined"
                sx={{ p: 2, mb: 2, borderRadius: 'sm' }}
            >
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.5}
                    flexWrap={'wrap'}
                    useFlexGap
                >
                    <FormControl size="sm" sx={{ justifyContent: 'flex-end' }}>
                        <Tooltip title={'Check for new changes'}>
                            <IconButton
                                size="sm"
                                variant="outlined"
                                color="neutral"
                                loading={isFetching}
                                onClick={() => setRefreshToken(Date.now())}
                                sx={{ bgcolor: 'background.surface' }}
                            >
                                <RefreshRoundedIcon/>
                            </IconButton>
                        </Tooltip>
                    </FormControl>

                    <FormControl size="sm">
                        <FormLabel>From</FormLabel>
                        <Input
                            type="date"
                            value={from}
                            onChange={(e) => resetPage(setFrom)(e.target.value)}
                        />
                    </FormControl>

                    <FormControl size="sm">
                        <FormLabel>To</FormLabel>
                        <Input
                            type="date"
                            value={to}
                            onChange={(e) => resetPage(setTo)(e.target.value)}
                        />
                    </FormControl>

                    {!!filters?.conductors?.length && (
                        <FormControl size="sm" sx={{ minWidth: 240 }}>
                            <FormLabel>Coordinating Program</FormLabel>
                            <Autocomplete
                                placeholder={'Select a program'}
                                options={filters.conductors}
                                getOptionLabel={(option) => option?.name || ''}
                                isOptionEqualToValue={(option, value) => option.id === value?.id}
                                value={conductor}
                                onChange={(e, value) => {
                                    setCommunity(null);
                                    resetPage(setConductor)(value);
                                }}
                                slotProps={{ listbox: { sx: { maxHeight: 320 } } }}
                            />
                        </FormControl>
                    )}

                    <FormControl size="sm" sx={{ minWidth: 240 }}>
                        <FormLabel>Community</FormLabel>
                        <Autocomplete
                            placeholder={'All communities'}
                            options={filters?.communities || []}
                            getOptionLabel={(option) => option?.name || ''}
                            isOptionEqualToValue={(option, value) => option.subdomain === value?.subdomain}
                            value={community}
                            onChange={(e, value) => resetPage(setCommunity)(value)}
                            slotProps={{ listbox: { sx: { maxHeight: 320 } } }}
                        />
                    </FormControl>

                    <FormControl size="sm" sx={{ minWidth: 180 }}>
                        <FormLabel>Category</FormLabel>
                        <Select
                            multiple
                            placeholder={'All categories'}
                            value={groups}
                            onChange={(e, value) => {
                                setTables([]);
                                resetPage(setGroups)(value);
                            }}
                        >
                            {(filters?.groups || []).map((group) => (
                                <Option key={group.key} value={group.key}>
                                    {group.label}
                                </Option>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="sm" sx={{ minWidth: 200 }}>
                        <FormLabel>Type</FormLabel>
                        <Select
                            multiple
                            placeholder={'All types'}
                            value={tables}
                            onChange={(e, value) => resetPage(setTables)(value)}
                            slotProps={{ listbox: { sx: { maxHeight: 320 } } }}
                        >
                            {availableTables.map((table) => (
                                <Option key={table.table} value={table.table}>
                                    {table.label}
                                </Option>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="sm" sx={{ minWidth: 200 }}>
                        <FormLabel>Changed by</FormLabel>
                        <Input
                            placeholder={'Name or email'}
                            value={userSearch}
                            onChange={(e) => resetPage(setUserSearch)(e.target.value)}
                            endDecorator={userSearch ? (
                                <IconButton
                                    size="sm"
                                    variant="plain"
                                    color="neutral"
                                    onClick={() => resetPage(setUserSearch)('')}
                                >
                                    <CloseRoundedIcon/>
                                </IconButton>
                            ) : null}
                        />
                    </FormControl>

                    <FormControl size="sm" sx={{ minWidth: 150 }}>
                        <FormLabel>Action</FormLabel>
                        <Select
                            multiple
                            placeholder={'All actions'}
                            value={actions}
                            onChange={(e, value) => resetPage(setActions)(value)}
                        >
                            {(filters?.actions || []).map((action) => (
                                <Option key={action} value={action}>
                                    {action.charAt(0).toUpperCase() + action.slice(1)}
                                </Option>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="sm" sx={{ justifyContent: 'flex-end' }}>
                        <Button
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            disabled={!hasFilters}
                            onClick={clearFilters}
                        >
                            Clear
                        </Button>
                    </FormControl>
                </Stack>
            </Sheet>

            {meta.truncated && (
                <Alert
                    color="warning"
                    variant="soft"
                    startDecorator={<WarningRoundedIcon/>}
                    sx={{ mb: 2 }}
                >
                    This range contains more changes than can be shown at once. Narrow the date range to see everything.
                </Alert>
            )}

            {(isLoading || filtersLoading) && <MaestroLoader/>}

            {!isLoading && !events.length && (
                <Sheet variant="soft" sx={{ p: 4, borderRadius: 'sm', textAlign: 'center' }}>
                    <Typography level="body-md">
                        {needsConductor
                            ? 'Select a coordinating program to see its audit log.'
                            : 'No changes found for these filters.'}
                    </Typography>
                </Sheet>
            )}

            {!isLoading && !!events.length && (
                <Sheet
                    variant="outlined"
                    sx={{ borderRadius: 'sm', overflow: 'auto', opacity: isFetching ? 0.6 : 1 }}
                >
                    <Table
                        stickyHeader
                        hoverRow
                        sx={{
                            '--Table-headerUnderlineThickness': '1px',
                            '--TableRow-hoverBackground': 'var(--joy-palette-background-level2)',
                            '& tr > *:first-of-type': { width: 48 },
                        }}
                    >
                        <thead>
                            <tr>
                                <th aria-label="Expand"></th>
                                <th style={{ width: 170 }}>When</th>
                                <th style={{ width: 180 }}>Community</th>
                                <th>What changed</th>
                                <th style={{ width: 170 }}>Changed by</th>
                            </tr>
                        </thead>
                        <tbody>
                            {events.map((event) => {
                                const key = auditEventKey(event);
                                const isOpen = !!expanded[key];
                                const changeCount = (event.changes || []).length;
                                const isSnapshot = event.action === 'created' || event.action === 'deleted';
                                const detailNames = new Set((event.changes || []).map((change) => change.detail_name).filter(Boolean));
                                const showDetailName = detailNames.size > 1;
                                const entityPath = auditEntityPath(event);

                                return (
                                    <>
                                        <tr key={key}>
                                            <td>
                                                {!!changeCount && (
                                                    <IconButton
                                                        size="sm"
                                                        variant="plain"
                                                        color="neutral"
                                                        onClick={() => toggleExpanded(key)}
                                                    >
                                                        {isOpen
                                                            ? <KeyboardArrowDownRoundedIcon/>
                                                            : <KeyboardArrowRightRoundedIcon/>}
                                                    </IconButton>
                                                )}
                                            </td>
                                            <td>
                                                <Typography fontSize="sm">{displayTimestamp(event.created_at, filters?.timezone)}</Typography>
                                            </td>
                                            <td>
                                                <Typography fontSize="sm">
                                                    {communityMap[event.subdomain] || event.subdomain}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                                    {entityPath ? (
                                                        <Link
                                                            fontSize="sm"
                                                            fontWeight="bold"
                                                            href={entityPath}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {event.entity_name}
                                                        </Link>
                                                    ) : (
                                                        <Typography fontSize="sm" fontWeight="bold">
                                                            {event.entity_name}
                                                        </Typography>
                                                    )}
                                                    {event.entity_deleted && (
                                                        <Chip size="sm" variant="soft" color="danger">deleted</Chip>
                                                    )}
                                                    <Typography fontSize="xs" textColor="neutral.500">
                                                        {event.entity_label}
                                                    </Typography>
                                                </Stack>
                                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                                    {(event.action_parts || []).map((part, index) => (
                                                        <Tooltip
                                                            key={`${key}-part-${index}`}
                                                            title={part.names?.length ? part.names.join(', ') : ''}
                                                            disableHoverListener={!part.names?.length}
                                                        >
                                                            <Chip
                                                                size="sm"
                                                                variant="soft"
                                                                color={auditActionColor(part.action)}
                                                            >
                                                                {part.text}
                                                            </Chip>
                                                        </Tooltip>
                                                    ))}
                                                    {!!changeCount && (
                                                        <Chip size="sm" variant="plain" color="neutral">
                                                            {changeCount} {isSnapshot ? (changeCount === 1 ? 'value' : 'values') : (changeCount === 1 ? 'field' : 'fields')}
                                                        </Chip>
                                                    )}
                                                </Stack>
                                            </td>
                                            <td>
                                                <Typography fontSize="sm">{event.user_name}</Typography>
                                            </td>
                                        </tr>
                                        {isOpen && (
                                            <tr key={`${key}-detail`}>
                                                <td colSpan={5} style={{ padding: 0 }}>
                                                    <Sheet variant="soft" sx={{ p: 2 }}>
                                                        <Table
                                                            size="sm"
                                                            sx={{ '--TableCell-paddingY': '4px', background: 'transparent' }}
                                                        >
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ width: isSnapshot ? '50%' : '34%' }}>Field</th>
                                                                    {!isSnapshot && <th style={{ width: '33%' }}>Before</th>}
                                                                    <th style={{ width: isSnapshot ? '50%' : '33%' }}>{isSnapshot ? 'Value' : 'After'}</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {(event.changes || []).map((change, index) => (
                                                                    <tr key={`${key}-change-${index}`}>
                                                                        <td>
                                                                            <Typography fontSize="xs" fontWeight="bold">
                                                                                {change.label}
                                                                            </Typography>
                                                                            {showDetailName && change.detail_name && (
                                                                                <Typography fontSize="xs" textColor="neutral.500">
                                                                                    {change.detail_name}
                                                                                </Typography>
                                                                            )}
                                                                        </td>
                                                                        {!isSnapshot && <td>{displayValue(change.from)}</td>}
                                                                        <td>{displayValue(event.action === 'deleted' ? change.from : change.to)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    </Sheet>
                                                </td>
                                            </tr>
                                        )}
                                    </>
                                );
                            })}
                        </tbody>
                    </Table>
                </Sheet>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Paginate
                    tint={'neutral'}
                    from={rangeFrom}
                    to={rangeTo}
                    total={total}
                    currentPage={currentPage}
                    perPage={perPage}
                    lastPage={lastPage}
                    onChange={setPage}
                />
            </Box>
        </Box>
    </>);
}